import { useTranslation } from 'react-i18next'
import { ArrowUpRight } from 'lucide-react'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

interface NewsCardProps {
  image: string
  imageAlt?: string
  title: string
  excerpt: string
  /** ISO 8601 date string (e.g. "2023-03-08") preferred for consistent formatting */
  date: string
  slug: string
  className?: string
}

function formatDate(dateStr: string, lang: SupportedLanguage): string {
  const parsed = new Date(dateStr)
  if (isNaN(parsed.getTime())) return dateStr
  return parsed.toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * NewsCard — editorial card for news articles.
 *
 * Design: image on top (16/9), date label with brand accent bar, bold title,
 * 2-line excerpt, "read more" affordance. Sharp corners. Same visual family
 * as MachineCard but leaner.
 */
export function NewsCard({
  image,
  imageAlt,
  title,
  excerpt,
  date,
  slug,
  className,
}: NewsCardProps) {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const currentLang = (i18n.language || 'it') as SupportedLanguage
  const newsSegment = localizedRoutes.news[currentLang]
  const href = `/${currentLang}/${newsSegment}/${slug}`

  const handleNavigate = () => {
    navigate(`${newsSegment}/${slug}`)
  }

  return (
    <article
      className={cn(
        'group relative flex h-full flex-col bg-white fx-card-border rounded-sm shadow-sm',
        'transition-[transform,box-shadow,border-color] duration-300 ease-out',
        'hover:-translate-y-0.5 hover:shadow-md hover:border-brand-400',
        className
      )}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden bg-ink-100">
        <img
          src={image}
          alt={imageAlt ?? title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900/30 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        />
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <div className="mb-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.15em] text-brand-600">
          <span aria-hidden="true" className="block h-px w-6 bg-brand-500" />
          <time dateTime={date}>{formatDate(date, currentLang)}</time>
        </div>

        <h3 className="text-lg md:text-xl font-bold leading-snug text-ink-900">
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              handleNavigate()
            }}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <span className="absolute inset-0" aria-hidden="true" />
            {title}
          </a>
        </h3>

        <p className="mt-2 text-[15px] text-ink-600 line-clamp-3 fx-text-pretty">
          {excerpt}
        </p>

        <div className="mt-auto pt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-600 transition-colors group-hover:text-brand-700">
          {t('buttons.readMore')}
          <ArrowUpRight
            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={2.5}
          />
        </div>
      </div>
    </article>
  )
}
